import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import AICoachFloating from "@/components/lingo/AICoachFloating";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Headphones, BookOpen, PencilLine, Mic, Bookmark, BookmarkCheck, Lock,
  Search, RotateCcw, ChevronLeft, ChevronRight
} from "lucide-react";

const SECTION_META = {
  LISTENING: { icon: Headphones, label: "Listening" },
  READING:   { icon: BookOpen,   label: "Reading" },
  WRITING:   { icon: PencilLine, label: "Writing" },
  SPEAKING:  { icon: Mic,        label: "Speaking" },
};

const PAGE_SIZE = 10;

// Visual "extra" items (locked) — simulate a fuller library beyond what backend seeds
const FILLER_ITEMS = [
  "Cue Support", "Restaurant Recommendation", "Australian Visa Status",
  "Travel Agency", "Short Stay Accommodation", "City Tour Booking", "Library Membership",
];

export default function MockTests() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");          // all / practiced / not_practiced
  const [bookmarkOnly, setBookmarkOnly] = useState(false);
  const [bookmarks, setBookmarks] = useState(() => JSON.parse(localStorage.getItem("lingoq_bookmarks") || "[]"));
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const section = params.get("section") || "LISTENING";
  const part = params.get("part") || "1";
  const kind = params.get("kind") || "";

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section, part, kind]);

  const load = async () => {
    const queryParams = {};
    if (section && section !== "ALL") queryParams.section = section;
    if (section === "LISTENING" && part) queryParams.part = part;
    if (section === "READING" && kind) queryParams.kind = kind;
    const r = await api.get("/tests/", { params: queryParams });
    setTests(r.data);
  };

  useEffect(() => { localStorage.setItem("lingoq_bookmarks", JSON.stringify(bookmarks)); }, [bookmarks]);

  const toggleBookmark = (id) => {
    setBookmarks((b) => b.includes(id) ? b.filter((x) => x !== id) : [...b, id]);
  };

  // Build the visible item list — real tests + filler locked items
  const realItems = tests.map((t) => ({
    id: t.id,
    number: `#${(900_000 + Math.abs(hashCode(t.id)) % 100_000).toString().padStart(7, "0")}`,
    title: t.title.replace(/^IELTS [^:]+: ?/i, ""),
    source: "Cambridge",
    locked: false,
  }));
  const lockedItems = FILLER_ITEMS.map((title, i) => ({
    id: `locked-${i}`,
    number: `#${(900_000 + 800 + i).toString().padStart(7, "0")}`,
    title,
    source: "Cambridge",
    locked: user?.subscription_status !== "active",
  }));
  let allItems = [...realItems, ...lockedItems];

  if (bookmarkOnly) allItems = allItems.filter((x) => bookmarks.includes(x.id));
  if (query.trim()) {
    const q = query.toLowerCase();
    allItems = allItems.filter((x) => x.title.toLowerCase().includes(q) || x.number.includes(q));
  }

  const totalQuestions = allItems.length;
  const pages = Math.max(1, Math.ceil(totalQuestions / PAGE_SIZE));
  const visible = allItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const SectionIcon = SECTION_META[section]?.icon || Headphones;
  const sectionLabel = SECTION_META[section]?.label || section;
  const headerTitle = section === "LISTENING"
    ? `Listening - Part ${part}`
    : section === "READING"
      ? `Reading - ${kind || "Academic"}`
      : sectionLabel;

  const reset = () => { setBookmarks([]); setBookmarkOnly(false); setFilter("all"); setQuery(""); setPage(1); };

  return (
    <DashboardLayout hideHeader>
      {/* Title row */}
      <div className="flex items-center justify-between mb-2 border-b border-[#F77F00]/30 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center">
            <SectionIcon className="w-4 h-4" />
          </div>
          <h1 data-testid="page-title" className="text-[#F77F00] font-display font-semibold text-lg">{headerTitle}</h1>
        </div>
        <button onClick={reset} data-testid="reset-practice" className="px-4 py-2 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white text-sm font-medium inline-flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset Practice
        </button>
      </div>

      {/* Filter row */}
      <div className="flex items-center justify-between mt-5 mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-5 text-sm">
          {[["all","All"],["practiced","Practiced"],["not_practiced","Not Practiced"]].map(([id, label]) => (
            <label key={id} className="inline-flex items-center gap-2 cursor-pointer text-slate-700">
              <input type="checkbox" checked={filter === id} onChange={() => setFilter(id)} data-testid={`filter-${id}`}
                className="w-4 h-4 accent-[#F77F00]" />
              {label}
            </label>
          ))}
        </div>
        <div className="relative">
          <input data-testid="content-search" placeholder="Content / Title / Number" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            className="pl-4 pr-11 py-2 rounded-full border border-[#F77F00]/40 text-sm w-64 focus:outline-none focus:border-[#F77F00]" />
          <button className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#F77F00] text-white flex items-center justify-center">
            <Search className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Secondary filters */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3 text-sm">
        <div className="flex items-center gap-5">
          <button onClick={() => setBookmarkOnly(false)} data-testid="tab-all" className={`pb-1 ${!bookmarkOnly ? "text-[#F77F00] border-b-2 border-[#F77F00]" : "text-slate-600"}`}>All</button>
          <button onClick={() => setBookmarkOnly(true)}  data-testid="tab-bookmark" className={`pb-1 ${bookmarkOnly ? "text-[#F77F00] border-b-2 border-[#F77F00]" : "text-slate-600"} inline-flex items-center gap-1`}>Bookmark ▾</button>
          <button className="pb-1 text-slate-600 inline-flex items-center gap-1">Source ▾</button>
        </div>
        <div className="flex items-center gap-3">
          <select className="text-sm border border-slate-200 rounded-full px-3 py-1.5 text-slate-700">
            <option>Question Type</option>
            <option>MCQ</option>
            <option>Gap fill</option>
            <option>Form completion</option>
          </select>
          <div className="text-sm text-slate-700">{totalQuestions} Questions</div>
        </div>
      </div>

      {/* Items list */}
      <div className="space-y-2">
        {visible.map((it) => {
          const bookmarked = bookmarks.includes(it.id);
          const Content = (
            <div className={`flex items-center justify-between gap-4 px-5 py-3 rounded-xl border ${
              it.locked ? "border-slate-200 bg-white" : "border-slate-200 bg-white hover:border-[#F77F00]/50"
            }`}>
              <div className={`flex items-center gap-6 min-w-0 ${it.locked ? "opacity-60 blur-[1px] select-none" : ""}`}>
                <span className="font-mono text-sm text-[#F77F00] shrink-0">{it.number}</span>
                <span className="text-[#003049] text-sm font-medium truncate">{it.title}</span>
              </div>
              <div className="flex items-center gap-3">
                {it.locked && <Lock className="w-3.5 h-3.5 text-[#F77F00]" />}
                <span className="text-[11px] px-2 py-0.5 rounded-md border border-[#F77F00] text-[#F77F00] font-medium">{it.source}</span>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleBookmark(it.id); }} data-testid={`bookmark-${it.id}`}>
                  {bookmarked ? <BookmarkCheck className="w-4 h-4 text-[#F77F00]" /> : <Bookmark className="w-4 h-4 text-slate-400 hover:text-[#F77F00]" />}
                </button>
              </div>
            </div>
          );
          return it.locked ? (
            <Link key={it.id} to="/pricing" data-testid={`item-${it.id}`}>{Content}</Link>
          ) : (
            <Link key={it.id} to={`/app/tests/${it.id}`} data-testid={`item-${it.id}`}>{Content}</Link>
          );
        })}
        {!visible.length && <div className="text-center py-12 text-slate-400">No items match your filters.</div>}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronLeft className="w-4 h-4 text-slate-500" /></button>
            {paginationPages(page, pages).map((p, i) => p === "…" ? (
              <span key={i} className="px-2 text-slate-400">…</span>
            ) : (
              <button key={i} onClick={() => setPage(p)} className={`min-w-[32px] h-8 rounded-lg text-sm font-medium ${
                p === page ? "bg-[#F77F00] text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
              }`}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"><ChevronRight className="w-4 h-4 text-slate-500" /></button>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            GO TO <input type="number" min="1" max={pages} value={page} onChange={(e) => { const n = Math.max(1, Math.min(pages, +e.target.value || 1)); setPage(n); }}
              className="w-14 px-2 py-1 rounded-full border border-slate-200 text-center" /> /{pages}
          </div>
        </div>
      )}
      <AICoachFloating />
    </DashboardLayout>
  );
}

function hashCode(s) { let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; } return h; }
function paginationPages(cur, total) {
  if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
  if (cur <= 3) return [1, 2, 3, 4, 5, "…", total];
  if (cur >= total - 2) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "…", cur - 1, cur, cur + 1, "…", total];
}
